import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewApi, aiApi } from '../../services/api';
import { Spinner, ConfirmModal, Modal } from '../../components/shared/index';
import { CheckIcon, ArrowLeftIcon, LightBulbIcon, LockClosedIcon, ClockIcon, SparklesIcon, ShieldExclamationIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import clsx from 'clsx';

const TYPE_LABELS = {
  manager: 'Manager review', self: 'Self review', peer: 'Peer review',
  upward: 'Upward review', skip_level: 'Skip-level review',
};

function formatDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return d; }
}

function TypeBadge({ type }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-50 text-purple-700 border border-purple-100">
      {TYPE_LABELS[type] || type}
    </span>
  );
}

function RatingInput({ value, onChange, scale = 5, readOnly }) {
  const nums = Array.from({ length: scale }, (_, i) => i + 1);
  return (
    <div className="flex gap-2 flex-wrap">
      {nums.map(n => (
        <button
          key={n}
          type="button"
          disabled={readOnly}
          onClick={() => onChange(n)}
          className={clsx(
            'w-11 h-11 rounded-lg text-sm font-semibold border transition-all',
            value === n
              ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
              : 'bg-white border-gray-200 text-gray-600 hover:border-blue-300 hover:bg-blue-50',
            readOnly && 'cursor-not-allowed opacity-80'
          )}
        >
          {n}
        </button>
      ))}
    </div>
  );
}

function YesNoInput({ value, onChange, readOnly }) {
  return (
    <div className="flex gap-2">
      {[['yes', 'Yes'], ['no', 'No']].map(([v, l]) => (
        <button
          key={v}
          type="button"
          disabled={readOnly}
          onClick={() => onChange(v)}
          className={clsx(
            'px-5 py-2 rounded-lg text-sm font-medium border transition-colors',
            value === v
              ? v === 'yes'
                ? 'bg-green-600 border-green-600 text-white'
                : 'bg-red-600 border-red-600 text-white'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50',
            readOnly && 'cursor-not-allowed opacity-80'
          )}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

function MultiSelectInput({ value = [], options = [], onChange, readOnly }) {
  const toggle = (opt) => {
    if (readOnly) return;
    onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  };
  return (
    <div className="flex flex-col gap-2">
      {options.map(opt => {
        const active = value.includes(opt);
        return (
          <label
            key={opt}
            className={clsx(
              'flex items-center gap-3 px-3 py-2 rounded-lg border text-sm cursor-pointer transition-colors',
              active ? 'border-blue-500 bg-blue-50 text-blue-800' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50',
              readOnly && 'cursor-not-allowed opacity-80'
            )}
          >
            <span className={clsx('w-4 h-4 rounded border flex items-center justify-center', active ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white')}>
              {active && <CheckIcon className="w-3 h-3 text-white" />}
            </span>
            <input type="checkbox" className="hidden" checked={active} onChange={() => toggle(opt)} disabled={readOnly} />
            {opt}
          </label>
        );
      })}
    </div>
  );
}

function QuestionRenderer({ q, value, onChange, readOnly }) {
  if (q.type === 'rating')
    return <RatingInput value={value ?? 0} onChange={onChange} scale={q.scale || 5} readOnly={readOnly} />;
  if (q.type === 'yes_no')
    return <YesNoInput value={value} onChange={onChange} readOnly={readOnly} />;
  if (q.type === 'multi_select')
    return <MultiSelectInput value={value || []} options={q.options || []} onChange={onChange} readOnly={readOnly} />;
  return (
    <textarea
      className="input min-h-[110px] resize-y"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="Type your response…"
      readOnly={readOnly}
      disabled={readOnly}
    />
  );
}

export default function ReviewForm() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const qc        = useQueryClient();
  const [answers, setAnswers] = useState({}); // { [question_id]: answer }
  const [summary, setSummary] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [aiDraft, setAiDraft] = useState(null);
  const [aiPowered, setAiPowered] = useState(null); // true | false | null
  const [biasFlags, setBiasFlags] = useState([]);
  const [biasChecking, setBiasChecking] = useState(false);

  const { data: review, isLoading } = useQuery({
    queryKey: ['review', id],
    queryFn:  () => reviewApi.get(id).then(r => r.data),
  });

  // hydrate from existing responses when review loads
  useEffect(() => {
    if (!review) return;
    const seeded = {};
    (review.responses || review.responses_tmp || []).forEach(r => {
      seeded[r.question_id] = r.answer;
    });
    setAnswers(seeded);
    setSummary(review.summary || '');
  }, [review]);

  const questions = useMemo(() => review?.template?.questions || [], [review]);
  const readOnly  = review?.status === 'submitted';

  const buildResponses = () => questions.map(q => {
    const answer = answers[q.id];
    const base   = { question_id: q.id, type: q.type, answer };
    if (q.type === 'rating' && typeof answer === 'number') base.score = answer;
    return base;
  });

  const numericScore = useMemo(() => {
    const ratings = questions.filter(q => q.type === 'rating');
    if (!ratings.length) return null;
    const scored = ratings.filter(q => typeof answers[q.id] === 'number');
    if (!scored.length) return null;
    const sum = scored.reduce((acc, q) => acc + answers[q.id], 0);
    return +(sum / scored.length).toFixed(2);
  }, [questions, answers]);

  const saveDraftMut = useMutation({
    mutationFn: () => reviewApi.update(id, { summary, responses_tmp: buildResponses() }),
    onSuccess:  () => { toast.success('Draft saved'); qc.invalidateQueries({ queryKey: ['review', id] }); },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  const draftMut = useMutation({
    mutationFn: () => aiApi.draftReview({
      employee_id: review?.employee_id || review?.employee?.id,
      cycle_id:    review?.cycle_id || review?.cycle?.id,
      type:        review?.type,
    }),
    onSuccess: (res) => {
      setAiDraft(res.data?.draft || '');
      setAiPowered(!!res.data?.ai_powered);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Failed to draft review'),
  });

  const biasMut = useMutation({
    mutationFn: (text) => aiApi.biasCheck(text),
    onSuccess: (res) => {
      setBiasFlags(res.data?.flags || []);
      setAiPowered((prev) => prev === null ? !!res.data?.ai_powered : prev);
      setBiasChecking(false);
    },
    onError: () => { setBiasChecking(false); },
  });

  // Debounced auto bias check
  useEffect(() => {
    if (!summary || summary.trim().length <= 50) {
      setBiasFlags([]);
      return;
    }
    setBiasChecking(true);
    const handle = setTimeout(() => biasMut.mutate(summary), 2000);
    return () => { clearTimeout(handle); setBiasChecking(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summary]);

  const submitMut = useMutation({
    mutationFn: () => reviewApi.submit(id, { responses: buildResponses(), numeric_score: numericScore, summary }),
    onSuccess:  () => {
      toast.success('Review submitted');
      qc.invalidateQueries({ queryKey: ['review', id] });
      qc.invalidateQueries({ queryKey: ['my-pending-reviews'] });
      qc.invalidateQueries({ queryKey: ['reviews'] });
      navigate('/app/reviews');
    },
    onError:    (e) => toast.error(e.response?.data?.message || 'Failed'),
  });

  if (isLoading) return <div className="flex justify-center p-20"><Spinner size="lg" /></div>;
  if (!review)   return <div className="p-6 max-w-3xl mx-auto"><p className="text-sm text-gray-500">Review not found.</p></div>;

  const missingRequired = questions
    .filter(q => q.required)
    .some(q => {
      const a = answers[q.id];
      if (q.type === 'multi_select') return !Array.isArray(a) || a.length === 0;
      if (q.type === 'rating')       return typeof a !== 'number';
      return !a || (typeof a === 'string' && !a.trim());
    });
  const canSubmit = !missingRequired && summary.trim().length > 0;

  const overdue = review.due_at && !review.submitted_at && new Date(review.due_at) < new Date();

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <Link to="/app/reviews" className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mb-4">
        <ArrowLeftIcon className="w-4 h-4" /> Back to reviews
      </Link>

      {/* Header card */}
      <div className="card p-5 mb-5">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-lg font-semibold">
              {review.employee?.name?.[0] || '?'}
            </div>
            <div>
              <h1 className="page-title">{review.employee?.name || 'Reviewee'}</h1>
              <p className="text-sm text-gray-500 capitalize">{review.employee?.role?.replace('_', ' ') || ''}{review.employee?.department ? ` · ${review.employee.department}` : ''}</p>
              <div className="flex items-center gap-2 mt-2">
                <TypeBadge type={review.type} />
                <span className="text-xs text-gray-500">Cycle: <span className="font-medium text-gray-700">{review.cycle?.name || '—'}</span></span>
              </div>
            </div>
          </div>
          <div className="text-right">
            {readOnly ? (
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-50 text-green-700 text-xs font-semibold border border-green-200">
                <LockClosedIcon className="w-3.5 h-3.5" />
                Submitted {formatDate(review.submitted_at)}
              </div>
            ) : (
              <div className={clsx('inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border', overdue ? 'bg-red-50 text-red-700 border-red-200' : 'bg-amber-50 text-amber-700 border-amber-200')}>
                <ClockIcon className="w-3.5 h-3.5" />
                Due {formatDate(review.due_at)}{overdue && ' · overdue'}
              </div>
            )}
            {typeof review.numeric_score === 'number' && (
              <p className="mt-2 text-xs text-gray-500">Score: <span className="font-mono font-semibold text-gray-800">{review.numeric_score.toFixed(2)}</span></p>
            )}
          </div>
        </div>
      </div>

      {/* Questions */}
      <div className="space-y-4">
        {questions.length === 0 && (
          <div className="card p-6 text-sm text-gray-500">This review has no template questions. Use the summary below to provide narrative feedback.</div>
        )}
        {questions.map((q, idx) => (
          <div key={q.id} className="card">
            <div className="card-header">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Question {idx + 1}{q.required && <span className="text-red-500 ml-1">*</span>}</p>
                  <h3 className="section-title">{q.label}</h3>
                </div>
                <span className="text-xs text-gray-400 uppercase tracking-wide">{q.type.replace('_', ' ')}</span>
              </div>
            </div>
            <div className="card-body">
              <QuestionRenderer
                q={q}
                value={answers[q.id]}
                onChange={(val) => setAnswers(a => ({ ...a, [q.id]: val }))}
                readOnly={readOnly}
              />
            </div>
          </div>
        ))}

        {/* Summary with SBI */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <h3 className="section-title">Overall summary{!readOnly && <span className="text-red-500 ml-1">*</span>}</h3>
              <div className="flex items-center gap-2">
                {aiPowered !== null && (
                  <span className={clsx(
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border',
                    aiPowered
                      ? 'bg-purple-50 text-purple-700 border-purple-200'
                      : 'bg-gray-50 text-gray-600 border-gray-200'
                  )}>
                    <SparklesIcon className="w-3 h-3" />
                    {aiPowered ? 'Powered by AI' : 'Lexical fallback'}
                  </span>
                )}
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => { setAiDraftOpen(true); setAiDraft(null); draftMut.mutate(); }}
                    disabled={draftMut.isPending}
                    className="btn-secondary text-xs disabled:opacity-40"
                  >
                    <SparklesIcon className="w-4 h-4" />
                    AI Draft
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="card-body space-y-3">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 flex gap-2">
              <LightBulbIcon className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <p className="font-semibold">Use the SBI framework for clear, specific feedback:</p>
                <p><span className="font-semibold">Situation:</span> Describe the context.</p>
                <p><span className="font-semibold">Behaviour:</span> What did you observe.</p>
                <p><span className="font-semibold">Impact:</span> What outcome followed.</p>
              </div>
            </div>
            <textarea
              className="input min-h-[160px] resize-y"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              placeholder="Example: During the Q3 migration sprint (situation), Jane proactively coordinated across three teams and unblocked backend dependencies daily (behaviour), which let us ship two weeks ahead of plan (impact)…"
              readOnly={readOnly}
              disabled={readOnly}
            />

            {!readOnly && (
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <button
                  type="button"
                  onClick={() => { setBiasChecking(true); biasMut.mutate(summary); }}
                  disabled={!summary.trim() || biasMut.isPending}
                  className="text-xs inline-flex items-center gap-1.5 text-gray-600 hover:text-gray-900 disabled:opacity-40"
                >
                  <ShieldExclamationIcon className="w-4 h-4" />
                  Check for bias
                </button>
                {biasChecking && <span className="text-xs text-gray-400 inline-flex items-center gap-1"><ArrowPathIcon className="w-3 h-3 animate-spin" /> Analysing…</span>}
              </div>
            )}

            {biasFlags.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">Potential bias detected ({biasFlags.length})</p>
                <div className="space-y-2">
                  {biasFlags.map((f, i) => (
                    <div key={i} className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                      <div className="flex items-start gap-2">
                        <ShieldExclamationIcon className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
                        <div className="flex-1">
                          <p className="font-semibold capitalize">{(f.type || 'bias').replace('_', ' ')}</p>
                          {f.quote && <p className="text-xs italic mt-1">"{f.quote}"</p>}
                          {f.suggestion && <p className="text-xs mt-1 text-amber-800">Suggestion: {f.suggestion}</p>}
                          {f.source && <p className="text-[10px] text-amber-600 mt-1">source: {f.source}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      {!readOnly && (
        <div className="sticky bottom-4 mt-6 bg-white border border-gray-200 rounded-xl shadow-sm px-4 py-3 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-gray-500">
            {missingRequired ? (
              <span className="text-amber-600">Some required questions are still unanswered.</span>
            ) : !summary.trim() ? (
              <span className="text-amber-600">Add a summary to submit.</span>
            ) : (
              <span className="text-green-600">Ready to submit.</span>
            )}
            {numericScore !== null && (
              <span className="ml-3 text-xs text-gray-500">Average rating: <span className="font-mono font-semibold text-gray-800">{numericScore.toFixed(2)}</span></span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => saveDraftMut.mutate()}
              disabled={saveDraftMut.isPending}
              className="btn-secondary disabled:opacity-40"
            >
              {saveDraftMut.isPending ? 'Saving…' : 'Save draft'}
            </button>
            <button
              onClick={() => setConfirming(true)}
              disabled={!canSubmit || submitMut.isPending}
              className="btn-primary disabled:opacity-40"
            >
              <CheckIcon className="w-4 h-4" />
              Submit review
            </button>
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => submitMut.mutate()}
        title="Submit this review?"
        message="Once submitted the review is locked and cannot be edited. It will be factored into the scoring and calibration for this cycle."
      />

      <Modal open={aiDraftOpen} onClose={() => setAiDraftOpen(false)} title="AI Draft" size="lg">
        {draftMut.isPending || (!aiDraft && !draftMut.isError) ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3">
            <Spinner />
            <p className="text-sm text-gray-500">Drafting…</p>
          </div>
        ) : draftMut.isError ? (
          <p className="text-sm text-red-600">Unable to generate draft. Try again.</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className={clsx(
                'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border',
                aiPowered ? 'bg-purple-50 text-purple-700 border-purple-200' : 'bg-gray-50 text-gray-600 border-gray-200'
              )}>
                <SparklesIcon className="w-3 h-3" />
                {aiPowered ? 'Powered by AI' : 'Lexical fallback'}
              </span>
            </div>
            <div className="whitespace-pre-wrap text-sm text-gray-800 bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[400px] overflow-y-auto">
              {aiDraft}
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => draftMut.mutate()} disabled={draftMut.isPending} className="btn-secondary disabled:opacity-40">
                <ArrowPathIcon className={clsx('w-4 h-4', draftMut.isPending && 'animate-spin')} />
                Regenerate
              </button>
              <button
                onClick={() => { setSummary(aiDraft); setAiDraftOpen(false); toast.success('Draft inserted into summary'); }}
                className="btn-primary"
              >
                Use draft
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
